import cn from 'classnames';
import startCase from 'lodash/startCase';
import { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/common/Button';
import DefaultLink from 'components/common/DefaultLink';
import DiscountTag from 'components/common/DiscountTag';
import ErrorMessage from 'components/common/ErrorMessage';
import Slideout from 'components/common/Slideout';
import StrikethroughSkeleton from 'components/common/StrikethroughSkeleton';
import Text from 'components/common/Text';
import ForkAndKnife from 'components/icons/ForkAndKnife.js';
import ArrowLeft from 'components/icons/navigation/ArrowLeft';
import { UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import Disclaimer from 'components/signUpFunnel/shared/Disclaimer';
import * as Routes from 'helpers/routes';
import { displayServingsText, displayFunnelServingsText } from 'helpers/servings';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { PICK_YOUR_MEALS_MINIMUM_ITEMS, TEMPO, STANDARD_MEAL_SERVING_SIZE } from 'lib/constants';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';
import styles from 'styles/components/signUpFunnel/box/OrderSummary';

import ClosedMobileOrderSummary from './ClosedMobileOrderSummary';
import PickMoreMealsModal from './PickMoreMealsModal';

const OrderSummary = ({
  orderSummary,
  isOrderSummaryOpen,
  toggleOrderSummary,
  selectedMeals,
  totalServings,
  meals,
  menuId = '',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [error, setError] = useState('');
  const errorRef = useRef();

  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
  });
  const openModal = type => setModalState({ isOpen: true, type: type });
  const closeModal = () => setModalState({ isOpen: false, type: null });

  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);
  const { isMobile, isTablet } = useScreenSizeConstants();
  const showDiscount =
    orderSummary.promoApplied &&
    orderSummary.subtotalFormatted !== orderSummary.promo.subtotalFormatted;

  const onContinue = async () => {
    setIsSubmitting(true);
    const response = await RequestService.createPickMealsBasket({
      params: {
        meals: selectedMeals.map(meal => ({ id: meal.id, servings: meal.servings })),
        menuId,
      },
    });
    if (response.ok) {
      setUser(prevUser => ({
        ...prevUser,
        weeklyMeals: response.user.weeklyMeals,
      }));
      navigate(Routes.PICK_MEALS_BILLING_PATH);
    } else {
      setError(response.error);
      setIsSubmitting(false);
    }
  };

  const handleOnClick = useAnalyticsCallback(
    async () => {
      setIsSubmitting(true);
      try {
        if (totalServings < PICK_YOUR_MEALS_MINIMUM_ITEMS) return openModal('lowMeals');
        if (
          totalServings >= PICK_YOUR_MEALS_MINIMUM_ITEMS &&
          totalServings < user?.weeklyMeals * STANDARD_MEAL_SERVING_SIZE
        ) {
          openModal('priceChange');
        } else {
          onContinue();
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    ({ logEvent, events }) => {
      logEvent(events.pickMeals[TEMPO].pickMeals.submitted, {
        mealPlanSlug: TEMPO,
        numberMealsPicked: displayFunnelServingsText({
          servings: totalServings,
          increment: 1,
        }),
        mealIds: selectedMeals.map(meal => meal.slug).join(', '),
      });
    },
    [onContinue, totalServings, selectedMeals, user?.weeklyMeals]
  );

  const Wrapper = isMobile || isTablet ? Slideout : 'div';
  const wrapperProps =
    isMobile || isTablet
      ? {
          show: isOrderSummaryOpen,
          id: 'pick-your-meals-order-summary-slideout',
          labelId: 'pick-your-meals-order-summary-slideout-label',
          hideTopBar: true,
          slideoutContainerClassname: 'px-4',
        }
      : {
          className: 'inset-0 flex flex-col overflow-y-auto bg-white-100 px-4 w-[400px]',
        };

  if (!isOrderSummaryOpen && (isMobile || isTablet))
    return (
      <ClosedMobileOrderSummary
        toggleOrderSummary={toggleOrderSummary}
        handleOnClick={handleOnClick}
        orderSummary={orderSummary}
        user={user}
        totalServings={totalServings}
        setModalState={setModalState}
        modalState={modalState}
        isSubmitting={isSubmitting}
        onContinue={onContinue}
        showDiscount={showDiscount}
      />
    );

  return (
    <Wrapper {...wrapperProps}>
      <div className="mt-6 flex justify-start">
        <Button
          variation="Ghost"
          className="px-0 pb-6 text-tempo-black-100 bp2:hidden"
          onClick={toggleOrderSummary}
        >
          <ArrowLeft className="mr-1 w-5 align-middle" /> Return to Menu
        </Button>
      </div>
      <Text
        brandStylesOverrides="h2"
        className="hidden p-6 text-center text-tempo-black-100 bp2:block"
      >
        First Box Summary
      </Text>
      <div className="rounded bg-tempo-gray-40 p-4">
        <div className="flex justify-between">
          <div>
            <Text className="justify-center rounded-3xl bg-tempo-gray-100 px-3 py-2 font-medium text-tempo-black-100">
              {displayFunnelServingsText({ servings: totalServings, increment: 1 })} /{' '}
              {user?.weeklyMeals} meals
            </Text>
          </div>
          <DefaultLink
            to="/our-menu?pym=true&box=true"
            isExternal
            className="textMd py-2 text-tempo-blue-90"
          >
            Change Box Size
          </DefaultLink>
        </div>
        {selectedMeals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6">
            <ForkAndKnife />
            <Text brandStylesOverrides="h5" className="pb-1 pt-4 text-tempo-black-100">
              Your box is waiting!
            </Text>
            <Text className="pb-6 text-center text-tempo-black-100">
              Take meal prep off your to-do list.
            </Text>
          </div>
        ) : (
          <div className="py-6">
            {selectedMeals.map(meal => (
              <div key={meal.id} className="mb-4 flex bp1:mb-3">
                <div className="mr-4">
                  <img className="max-w-[100px] rounded-lg" src={meal.photoUrl} alt={meal.title} />
                </div>
                <div
                  className={cn('max-w-60', {
                    'flex flex-col justify-center': isMobile,
                  })}
                >
                  <Text className="mb-0.5 bp1:mb-0" brandStylesOverrides="textMd text-charcoal-100">
                    {meal.title}
                  </Text>
                  <Text className="text-charcoal-90">
                    {startCase(
                      displayServingsText({
                        servings: meal.servings,
                        displayUnit: meals.find(m => m.id === meal.id).displayUnit,
                      })
                    )}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex border-t border-solid border-charcoal-60 pt-6">
          <Text
            htmlElement="dt"
            brandStylesOverrides="textMd"
            className={cn('flex-1 pb-2 text-tempo-black-100', styles.textModule)}
          >
            Subtotal
          </Text>
          {showDiscount ? (
            <>
              <DiscountTag
                discountText={orderSummary.promo.displayedDiscount}
                spanClassNames="flex-none"
              />
              <StrikethroughSkeleton
                strikethroughText={orderSummary.subtotalFormatted}
                textDisplayed={orderSummary.promo.subtotalFormatted}
                textDisplayedClassName="textMd text-secondary"
                ariaLabel="subtotal"
              />
            </>
          ) : (
            <Text htmlElement="span" brandStylesOverrides="textMd">
              {orderSummary.subtotalFormatted}
            </Text>
          )}
        </div>
        <div className="flex">
          <Text
            htmlElement="dt"
            brandStylesOverrides="textMd"
            className="flex-1 text-tempo-black-100"
          >
            Shipping
          </Text>
          {orderSummary.shippingPromoApplied ? (
            <StrikethroughSkeleton
              strikethroughText={orderSummary.shippingCostFormatted}
              textDisplayed="FREE"
              ariaLabel="shipping"
            />
          ) : (
            <Text htmlElement="span" brandStylesOverrides="textMd">
              {orderSummary.shippingCostFormatted}
            </Text>
          )}
        </div>
      </div>
      <div className="mb-3 mt-6">
        <CSSTransition
          nodeRef={errorRef}
          classNames="fadeInOut"
          in={Boolean(error)}
          timeout={300}
          unmountOnExit
        >
          <div ref={errorRef} className="max-w-sm">
            <ErrorMessage message={error} />
          </div>
        </CSSTransition>
        <Button
          type="submit"
          onClick={handleOnClick}
          className="w-full"
          isSubmitting={isSubmitting}
        >
          Continue to Billing
        </Button>
        <PickMoreMealsModal
          totalServings={totalServings}
          userServings={user?.servings}
          isOpen={modalState.isOpen}
          onDismiss={closeModal}
          type={modalState.type}
          isSubmitting={isSubmitting}
          onContinue={onContinue}
        />
      </div>
      <Text className="textSm pb-2 text-charcoal-90">
        You can select your delivery date on the next page.
      </Text>
      {voucher?.disclaimerMessage && (
        <Disclaimer
          viewPromoDisclaimerCopy
          message={voucher.disclaimerMessage}
          buttonClassNames="textSm p-0"
          textClassNames="pt-1"
        />
      )}
    </Wrapper>
  );
};

OrderSummary.propTypes = {
  isOrderSummaryOpen: PropTypes.bool.isRequired,
  toggleOrderSummary: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  selectedMeals: PropTypes.array.isRequired,
  totalServings: PropTypes.number.isRequired,
  meals: PropTypes.array.isRequired,
  menuId: PropTypes.string,
  showDiscount: PropTypes.bool,
};

export default OrderSummary;
